import { useSyncExternalStore } from "react";

/**
 * Actor-side "last touched" stamps for the lead card's top-right corner.
 *
 * The corner shows `Leads.updatedAt`, which the server bumps on ANY activity —
 * but that bump rides the fire-and-forget activity-log write, so the actor's
 * own `["leads"]` refetch can race ahead of it (and then sit in the server's
 * list cache). The card therefore also reads the moment the CURRENT user last
 * acted on the lead and shows whichever is newer. A stamp lives outside React
 * Query on purpose: a stale refetch can't roll it back. Once the server's
 * value passes it, the server wins again.
 */
const stamps = new Map<number, string>();
const listeners = new Set<() => void>();

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function touchLead(leadId: number | string | null | undefined) {
  const id = Number(leadId);
  if (leadId == null || leadId === "" || !Number.isFinite(id)) return;
  stamps.set(id, new Date().toISOString());
  listeners.forEach((l) => l());
}

/** Newer of the server's `updatedAt` and the actor's local stamp. */
export function useFreshestUpdatedAt(
  leadId: number | string | null | undefined,
  updatedAt: string | null | undefined,
): string | null | undefined {
  const local = useSyncExternalStore(subscribe, () =>
    leadId == null ? undefined : stamps.get(Number(leadId)),
  );
  if (!local) return updatedAt;
  if (!updatedAt) return local;
  return new Date(local).getTime() > new Date(updatedAt).getTime()
    ? local
    : updatedAt;
}
